// useTerminology — the single source of truth for vertical-agnostic labels.
//
// Every screen that renders a hard-coded word like "Tech" or "Job" should use
// this hook instead, so the UI auto-adapts:
//   - Real Estate signup sees "Agent" + "Showing" + "Client"
//   - Salon signup sees "Stylist" + "Appointment" + "Client"
//   - Vet signup sees "Vet" + "Appointment" + "Patient"
//   - HVAC signup keeps "Tech" + "Job" + "Customer"
//
// The terminology comes from account.terminology (set at signup based on the
// vertical pack). Fallbacks default to trades language so screens that haven't
// been updated yet still work fine.

import { useEffect, useState } from 'react';

const TRADES_FALLBACK = {
  worker: 'tech',
  workers: 'techs',
  customer: 'customer',
  customers: 'customers',
  visit: 'job',
  visits: 'jobs',
};

// Capitalize-first helper. "tech" → "Tech", "showing" → "Showing".
function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
function pluralize(s) {
  if (!s) return s;
  if (s.endsWith('y')) return s.slice(0, -1) + 'ies';
  if (/(s|x|z|ch|sh)$/.test(s)) return s + 'es';
  return s + 's';
}

// Cached account terminology — refreshes when `tradeops:user-loaded` fires.
let cached = null;
function getCachedTerminology() {
  if (cached) return cached;
  if (typeof window === 'undefined') return TRADES_FALLBACK;
  try {
    const raw = localStorage.getItem('tradeops:terminology');
    if (raw) { cached = JSON.parse(raw); return cached; }
  } catch {}
  return TRADES_FALLBACK;
}

// Called by App.jsx whenever the user/account loads so the cache stays fresh.
export function setTerminology(t) {
  cached = { ...TRADES_FALLBACK, ...(t || {}) };
  // Auto-derive plurals when only singular provided
  for (const key of ['worker', 'customer', 'visit']) {
    const pluralKey = key + 's';
    if (!t?.[pluralKey] && t?.[key]) cached[pluralKey] = pluralize(t[key]);
  }
  try { localStorage.setItem('tradeops:terminology', JSON.stringify(cached)); } catch {}
  try { window.dispatchEvent(new CustomEvent('tradeops:terminology-changed')); } catch {}
}

export function clearTerminology() {
  cached = null;
  try { localStorage.removeItem('tradeops:terminology'); } catch {}
}

// React hook for components. Returns { t, cap, plural } where:
//   t('visit')        → 'job' (HVAC) or 'showing' (Real Estate) etc.
//   cap('visit')      → 'Job' / 'Showing'
//   plural('visit')   → 'jobs' / 'showings'
//   t.worker, t.visit etc. also work as direct property access for JSX brevity.
export function useTerminology() {
  const [terms, setTerms] = useState(getCachedTerminology());
  useEffect(() => {
    const sync = () => setTerms({ ...getCachedTerminology() });
    window.addEventListener('tradeops:terminology-changed', sync);
    return () => window.removeEventListener('tradeops:terminology-changed', sync);
  }, []);

  const t = (key) => terms[key] ?? TRADES_FALLBACK[key] ?? key;
  // Allow `t.worker` direct access for terser JSX
  t.worker    = terms.worker    ?? TRADES_FALLBACK.worker;
  t.workers   = terms.workers   ?? TRADES_FALLBACK.workers;
  t.customer  = terms.customer  ?? TRADES_FALLBACK.customer;
  t.customers = terms.customers ?? TRADES_FALLBACK.customers;
  t.visit     = terms.visit     ?? TRADES_FALLBACK.visit;
  t.visits    = terms.visits    ?? TRADES_FALLBACK.visits;

  const capT    = (key) => cap(t(key));
  const pluralT = (key) => terms[key + 's'] ?? pluralize(t(key));
  return { t, cap: capT, plural: pluralT, raw: terms };
}
